import React from 'react';
import { LoadingButton, Spinner } from './LoadingIndicators';
import { useInfiniteScroll } from '../hooks/useInfiniteScroll';

interface LoadMorePaginationProps {
  hasMore: boolean;
  isLoading: boolean;
  onLoadMore: () => void;
  totalCount?: number;
  currentCount?: number;
  infiniteScrollEnabled?: boolean;
  loadMoreText?: string;
  completionMessage?: string;
  className?: string;
}

/**
 * Enhanced Load More pagination component with infinite scroll support
 * Provides smooth animations, loading states, and completion messages
 */
export const LoadMorePagination: React.FC<LoadMorePaginationProps> = ({
  hasMore,
  isLoading,
  onLoadMore,
  totalCount,
  currentCount,
  infiniteScrollEnabled = true,
  loadMoreText = 'Load More Treasures',
  completionMessage = 'You\'ve reached the end! No more treasures to load.',
  className = ''
}) => {
  const { sentinelRef } = useInfiniteScroll({
    hasMore,
    isLoading,
    onLoadMore,
    threshold: 200,
    rootMargin: '100px',
    enabled: infiniteScrollEnabled
  });

  // Don't render anything if there are no items
  if (currentCount === 0 && !isLoading) {
    return null;
  }

  return (
    <div className={`text-center py-6 ${className}`}>
      {/* Loading state */}
      {isLoading && (
        <div className="flex flex-col items-center gap-3 animate-fade-in">
          <Spinner size="lg" />
          <p className="text-urban/70 text-sm animate-pulse">
            Loading more treasures...
          </p>
        </div>
      )}

      {/* Load More button - shown when not loading and has more content */}
      {!isLoading && hasMore && (
        <div className="space-y-4 animate-fade-in">
          {/* Progress indicator */}
          {totalCount && currentCount && (
            <div className="text-sm text-urban/60">
              Showing {currentCount.toLocaleString()} of {totalCount.toLocaleString()} treasures
            </div>
          )}
          
          <LoadingButton
            isLoading={false}
            onClick={onLoadMore}
            size="lg"
            className="px-8 py-4 text-base transition-all duration-300 hover:scale-105 hover:shadow-lg"
          >
            {loadMoreText}
          </LoadingButton>
          
          {infiniteScrollEnabled && (
            <p className="text-xs text-urban/50">
              Or scroll down to load more automatically
            </p>
          )}
        </div>
      )}

      {/* Completion message - shown when no more content */}
      {!isLoading && !hasMore && currentCount && currentCount > 0 && (
        <div className="space-y-3 animate-fade-in">
          <div className="flex items-center justify-center gap-2 text-urban/60">
            <span className="text-2xl">🎉</span>
            <p className="text-sm font-medium">
              {completionMessage}
            </p>
          </div>
          
          {totalCount && currentCount && (
            <div className="text-xs text-urban/50">
              Loaded all {currentCount.toLocaleString()} treasures
            </div>
          )}
        </div>
      )}

      {/* Infinite scroll sentinel - invisible element to trigger loading */}
      {infiniteScrollEnabled && hasMore && !isLoading && (
        <div
          ref={sentinelRef}
          className="h-1 w-full"
          aria-hidden="true"
        />
      )}
    </div>
  );
};

/**
 * Compact version for mobile or sidebar use
 */
export const CompactLoadMore: React.FC<LoadMorePaginationProps> = ({
  hasMore,
  isLoading,
  onLoadMore,
  currentCount,
  infiniteScrollEnabled = true,
  loadMoreText = 'Load More',
  completionMessage = 'All treasures loaded',
  className = ''
}) => {
  const { sentinelRef } = useInfiniteScroll({
    hasMore,
    isLoading,
    onLoadMore,
    threshold: 100,
    rootMargin: '50px',
    enabled: infiniteScrollEnabled
  });

  if (currentCount === 0 && !isLoading) {
    return null;
  }

  return (
    <div className={`p-4 border-t border-urban/10 ${className}`}>
      {/* Loading state */}
      {isLoading && (
        <div className="flex items-center justify-center gap-2">
          <Spinner size="sm" />
          <span className="text-sm text-urban/70">Loading...</span>
        </div>
      )}

      {/* Load More button */}
      {!isLoading && hasMore && (
        <LoadingButton
          isLoading={false}
          onClick={onLoadMore}
          className="w-full"
          variant="secondary"
        >
          {loadMoreText}
        </LoadingButton>
      )}

      {/* Completion message */}
      {!isLoading && !hasMore && currentCount && currentCount > 0 && (
        <div className="text-center text-sm text-urban/60">
          <span className="mr-1">✓</span>
          {completionMessage}
        </div>
      )}

      {/* Infinite scroll sentinel */}
      {infiniteScrollEnabled && hasMore && !isLoading && (
        <div
          ref={sentinelRef}
          className="h-1 w-full"
          aria-hidden="true"
        />
      )}
    </div>
  );
};

/**
 * Grid-specific load more component with fade-in animations for new items
 */
export const GridLoadMore: React.FC<LoadMorePaginationProps & {
  onItemsLoaded?: (newItemsCount: number) => void;
}> = ({
  hasMore,
  isLoading,
  onLoadMore,
  totalCount,
  currentCount,
  infiniteScrollEnabled = true,
  loadMoreText = 'Load More Treasures',
  completionMessage = 'You\'ve discovered all available treasures!',
  className = '',
  onItemsLoaded
}) => {
  const { sentinelRef } = useInfiniteScroll({
    hasMore,
    isLoading,
    onLoadMore,
    threshold: 300,
    rootMargin: '150px',
    enabled: infiniteScrollEnabled
  });

  // Trigger callback when new items are loaded
  React.useEffect(() => {
    if (!isLoading && currentCount && onItemsLoaded) {
      onItemsLoaded(currentCount);
    }
  }, [isLoading, currentCount, onItemsLoaded]);

  if (currentCount === 0 && !isLoading) {
    return null;
  }

  return (
    <div className={`col-span-full ${className}`}>
      {/* Loading skeleton grid */}
      {isLoading && (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6 mb-8">
          {Array.from({ length: 8 }).map((_, index) => (
            <div
              key={index}
              className="bg-gradient-to-r from-teddy-100 via-teddy-200 to-teddy-100 animate-shimmer bg-[length:200%_100%] rounded-lg h-64"
            />
          ))}
        </div>
      )}

      {/* Load More section */}
      <div className="text-center py-8">
        {!isLoading && hasMore && (
          <div className="space-y-4 animate-fade-in">
            {/* Progress bar */}
            {totalCount && currentCount && (
              <div className="max-w-md mx-auto">
                <div className="flex justify-between text-sm text-urban/60 mb-2">
                  <span>{currentCount.toLocaleString()} loaded</span>
                  <span>{totalCount.toLocaleString()} total</span>
                </div>
                <div className="w-full bg-urban/10 rounded-full h-2">
                  <div
                    className="bg-gradient-to-r from-cardboard to-treasure h-2 rounded-full transition-all duration-500 ease-out"
                    style={{ width: `${Math.min((currentCount / totalCount) * 100, 100)}%` }}
                  />
                </div>
              </div>
            )}
            
            <LoadingButton
              isLoading={false}
              onClick={onLoadMore}
              size="lg"
              className="px-12 py-4 text-base transition-all duration-300 hover:scale-105 hover:shadow-xl"
            >
              <span className="mr-2">🔍</span>
              {loadMoreText}
            </LoadingButton>
            
            {infiniteScrollEnabled && (
              <p className="text-xs text-urban/50 max-w-sm mx-auto">
                Keep scrolling to automatically discover more treasures as you browse
              </p>
            )}
          </div>
        )}

        {/* Completion celebration */}
        {!isLoading && !hasMore && currentCount && currentCount > 0 && (
          <div className="space-y-4 animate-fade-in">
            <div className="text-6xl mb-4">🏆</div>
            <h3 className="text-xl font-semibold text-urban mb-2">
              Treasure Hunt Complete!
            </h3>
            <p className="text-urban/70 mb-4 max-w-md mx-auto">
              {completionMessage}
            </p>
            
            {totalCount && currentCount && (
              <div className="inline-flex items-center gap-2 bg-treasure/10 text-treasure-800 px-4 py-2 rounded-full text-sm font-medium">
                <span>🎯</span>
                Explored {currentCount.toLocaleString()} treasures
              </div>
            )}
          </div>
        )}
      </div>

      {/* Infinite scroll sentinel */}
      {infiniteScrollEnabled && hasMore && !isLoading && (
        <div
          ref={sentinelRef}
          className="h-1 w-full"
          aria-hidden="true"
        />
      )}
    </div>
  );
};

export default LoadMorePagination;